import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { isEmpty } from 'lodash';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { bufferWhen, debounceTime, map } from 'rxjs/operators';
import { Project } from 'src/app/core/objects/project';
import { ColorsService } from 'src/app/core/services/colors.service';
import { NotificationsService } from 'src/app/core/services/notifications.service';
import { ProjectService } from 'src/app/core/services/project.service';
import { SharedProjectService } from 'src/app/core/services/shared-project.service';
import { Load } from 'src/app/working-table/visualisation/load';
import { ProjectApiService } from './../../../../core/services/project-api.service';
import { Group } from './../../../../core/types';
import { FADE_IN_OUT_ANIMATION } from './../../../../shared/animations/animations';
import { ColorPipe } from './../../../../shared/pipes/color.pipe';
import { MaxLoadsPopupComponent } from '../../../../modal/components/maxLoadsPopup/max-loads-popup.component';
import { ModalService } from '../../../../modal/modal.service';
import {
  getFromLocalStorage,
  isMoreThanDaysAgo,
  setLocalStorage,
} from '../../../utils/localStorageHelpers';
import { SharedChangesEditPopupComponent } from '../../../../container/shared-changes-edit-popup/shared-changes-edit-popup.component';
import { MessageService } from '../../../../core/services/message.service';
import { CookieService } from 'ngx-cookie-service';
import {
  palletSpaceQuantity,
  replicatedLoadCountLabel,
} from 'src/app/shared/helpers/pallet-workspace-collapse.helper';

@Component({
    selector: 'gl-loads-list-element',
    templateUrl: './loads-list-element.component.html',
    styleUrls: ['./loads-list-element.component.scss'],
    animations: [FADE_IN_OUT_ANIMATION],
    standalone: false
})
export class LoadsListElementComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() public group: Group;
  @Input() public workSpaceIndex: number;
  @Input() public stopIndex: number;
  @Input() public cargoSpaceIndex: number;
  @Input() public isUnloadingSpace: boolean;
  @Input() public isOutsideSpace: boolean;
  @Input() public isSharedProjectLast: boolean;
  @Input() public lastLoad: boolean;
  @Input() public interactionDisabled = false;
  @Output() public editwindow = new EventEmitter<boolean>();
  public quantity: number;
  public isMobile: boolean;
  public project: Project;
  @HostBinding('class.overlay') public isSharedView: boolean;
  @HostBinding('class.active') public edit = false;
  @HostBinding('class.current') public currentLoadOnMouse = false;
  @ViewChild('editTrigger', { static: false, read: ElementRef }) public editTriggerEl: ElementRef;
  @ViewChild('edit', { static: false, read: ElementRef })
  private editWindow: ElementRef<HTMLElement>;
  @ViewChild('minus', { static: false }) private minusElement: ElementRef;
  @ViewChild('plus', { static: false }) private plusElement: ElementRef;
  private subscriptions: Subscription[] = [];
  private currentWorkSpace = 0;
  public generatedImage = null;

  constructor(
    private projectService: ProjectService,
    private projectApiService: ProjectApiService,
    private sharedProjectService: SharedProjectService,
    private renderer: Renderer2,
    private elRef: ElementRef<HTMLElement>,
    private router: Router,
    private colorPipe: ColorPipe,
    private colorsService: ColorsService,
    private notificationService: NotificationsService,
    private modalService: ModalService,
    private messageService: MessageService,
    private cookieService: CookieService,
  ) {
    this.isMobile = this.router.url.includes('mobile');
    this.projectService.currentWorkSpaceIndex.subscribe(
      (currentWorkSpace: number) => (this.currentWorkSpace = currentWorkSpace),
    );
    this.projectService.project.subscribe((project) => (this.project = project));
  }

  public loadQuantityLabel(): string {
    return this.replicatedLoadsLabel() ?? `${this.quantity}`;
  }

  public showReplicatedLoadHint(): boolean {
    return this.replicatedLoadsLabel() != null;
  }

  public ngOnInit() {
    this.quantity = this.group.quantity;
    this.subscriptions.push(
      this.projectService.currentLoadGroupId.subscribe(
        (currentLoadGroup: { id: number; object: Load }) => {
          if (currentLoadGroup.id === this.group.id) {
            if (
              currentLoadGroup.object &&
              this.cargoSpaceIndex === currentLoadGroup.object.packed
            ) {
              this.currentLoadOnMouse = true;
            } else {
              this.currentLoadOnMouse = false;
            }
          } else {
            this.currentLoadOnMouse = false;
          }
        },
      ),
    );
    this.subscriptions.push(
      this.sharedProjectService.isViewingShared.subscribe((result) => (this.isSharedView = result)),
    );
    this.generatedImage = this.getMultistopUrlImage(
      this.getStopLetterFromIndex(this.getStopIndexFromId(this.group.destination?.id)),
      this.group.color,
    );
    this.subscriptions.push(
      this.messageService.getMessages().subscribe((message) => {
        if (message.name === 'stackingChange') {
          this.edit = !this.edit;
          this.editGroup();
        }
      }),
    );
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  public ngAfterViewInit() {
    if (!this.isMobile && !this.interactionDisabled) {
      this.createObservableForMinusEvent(this.minusElement);
      this.createObservableForPlusEvent(this.plusElement);
    }
  }

  @HostListener('mouseenter')
  public onMouseEnter() {
    if (isEmpty(document.getElementsByClassName('sortable-ghost'))) {
      this.projectService.setCurrentLoadGroupId(this.group.id);
      const isHighlightOn = this.cookieService.get('backlight').includes('true');
      if (isHighlightOn) {
        this.messageService.sendMessage({ name: 'startShortAnimation' });
      }
    }
  }

  @HostListener('mouseleave')
  public onMouseLeave() {
    this.projectService.setCurrentLoadGroupId(undefined);
  }

  public onClose() {
    this.edit = false;
    this.editwindow.emit(this.edit);
  }

  public getColor() {
    return this.colorPipe.transform(this.group.color);
  }

  public getMultistopUrlImage(destination: string, color: number) {
    return this.colorsService.getMultistopImageWithDestination(
      destination,
      color,
      this.isUnloadingSpace,
    );
  }

  public getStopIndexFromId(id: number) {
    return this.project.workSpaces[this.currentWorkSpace].stops?.findIndex(
      (stop) => stop.stopId === id,
    );
  }

  public getStopLetterFromIndex(index: number) {
    index = index !== undefined ? index : 0;
    return (index + 10).toString(36);
  }

  public editGroup() {
    this.edit = !this.edit;
    this.editwindow.emit(this.edit);
    if (this.edit) {
      window.setTimeout(() => {
        this.renderer.setStyle(this.editWindow.nativeElement, 'left', '450px');
        this.renderer.setStyle(
          this.editWindow.nativeElement,
          'top',
          this.calculateEditWindowTopPosition() + 'px',
        );
        this.renderer.setStyle(this.editWindow.nativeElement, 'visibility', 'visible');
      }, 0);
    }
  }

  private calculateEditWindowTopPosition(): number {
    const listElementRect: DOMRect = this.elRef.nativeElement.getBoundingClientRect();
    const editWindowChildRect: DOMRect =
      this.editWindow.nativeElement.children[0].getBoundingClientRect();
    const NAVBAR_HEIGHT = 50;
    let top: number = listElementRect.top - NAVBAR_HEIGHT;
    const isBottomVisible = top + editWindowChildRect.height + NAVBAR_HEIGHT < window.innerHeight;
    if (!isBottomVisible) {
      const topDiff: number = top + editWindowChildRect.height + 70 - window.innerHeight;
      top = top - topDiff;
    }
    const isTopVisible: boolean = top > 0;
    if (!isTopVisible) {
      top = 0;
    }
    return top;
  }

  public isLoadNotDisplayed(): boolean {
    const groupWithoutPosition = this.group.loadsCubes.find((load) => !load.position);
    const cargoSpace = this.project.workSpaces[this.workSpaceIndex].cargoSpaces[0];
    const cargoSpaceSet = this.project.workSpaces[this.currentWorkSpace].cargoSpaces[1];
    if (groupWithoutPosition && cargoSpace) {
      const cargoSpaceLength = cargoSpaceSet
        ? cargoSpaceSet.dimensions.length + cargoSpace.dimensions.length + cargoSpace.dimensions.gap
        : cargoSpace.dimensions.length;
      const cargoSpaceWidth = cargoSpaceSet
        ? cargoSpaceSet.dimensions.width > cargoSpace.dimensions.width
          ? cargoSpaceSet.dimensions.width + 400
          : cargoSpace.dimensions.width + 400
        : cargoSpace.dimensions.width + 400;
      const cargoSpaceHeight = cargoSpaceSet
        ? cargoSpaceSet.dimensions.height > cargoSpace.dimensions.height
          ? cargoSpaceSet.dimensions.height
          : cargoSpace.dimensions.height
        : cargoSpace.dimensions.height;
      return (
        this.group.dimensions.length > 2 * cargoSpaceLength ||
        this.group.dimensions.width > cargoSpaceWidth ||
        this.group.dimensions.height > cargoSpaceHeight
      );
    }
  }

  private currentPalletQuantity(): number {
    return palletSpaceQuantity(this.project?.workSpaces?.[this.workSpaceIndex]);
  }

  private replicatedLoadsLabel(): string | null {
    if (this.isOutsideSpace || this.isUnloadingSpace) {
      return null;
    }
    return replicatedLoadCountLabel(this.quantity, this.currentPalletQuantity());
  }

  private editLoadQuantity(quantityDifference: number) {
    const cargoSpaceId =
      this.cargoSpaceIndex >= 0
        ? this.project.workSpaces[this.workSpaceIndex].cargoSpaces[this.cargoSpaceIndex].id
        : undefined;
    this.projectApiService
      .editGroup({
        currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
        target: {
          workSpaceId: this.project.workSpaces[this.workSpaceIndex].id,
          stopId:
            this.stopIndex >= 0
              ? this.project.workSpaces[this.workSpaceIndex].stops[this.stopIndex].id
              : undefined,
          cargoSpaceId: cargoSpaceId,
          groupId: this.group.id,
        },
        quantityDifference,
        load: {},
      })
      .subscribe((result) => {
        this.quantity = this.group.quantity;
        if (result) {
          this.notificationService.generateCOGNotification(result);
        }
      });
  }

  private displaySharedChangesEditPopup(): Observable<any> {
    return this.modalService.open(SharedChangesEditPopupComponent);
  }

  private createObservableForPlusEvent(element: ElementRef<HTMLElement>) {
    const plus: Observable<Event> = fromEvent(element.nativeElement, 'click');
    this.subscriptions.push(
      plus
        .pipe(
          map(() =>
            !(this.projectService.currentNumberOfLoads + 1 > this.projectService.maxNumberOfLoads)
              ? this.quantity++
              : this.quantity,
          ),
          bufferWhen(() => plus.pipe(debounceTime(300))),
        )
        .subscribe((data: number[]): void => {
          this.plusMinus(data);
        }),
    );
  }

  private plusMinus(data: number[], which = 'plus'): void {
    const localStorageData: { data: string; date: string } = getFromLocalStorage('addLoadsList');
    const isPassedDays: boolean = localStorageData
      ? isMoreThanDaysAgo(new Date(localStorageData.date), 1)
      : false;
    if (isPassedDays) {
      localStorage.removeItem('addLoadsList');
    }
    const isPopupTouched: boolean = localStorageData ? localStorageData.data === 'true' : false;
    if (!this.isSharedProjectLast && !isPopupTouched) {
      this.displaySharedChangesEditPopup().subscribe((result): void => {
        setLocalStorage('true', 'addLoadsList');
        if (result.data === 'no') {
          !(this.projectService.currentNumberOfLoads + 1 > this.projectService.maxNumberOfLoads)
            ? which === 'plus'
              ? this.quantity--
              : this.quantity++
            : this.quantity;
          return;
        } else {
          this.plusEvent(data, which);
        }
      });
    } else {
      this.plusEvent(data, which);
    }
  }

  private plusEvent(data: number[], which = 'plus'): void {
    const isLoadLimitReached: boolean =
      this.projectService.currentNumberOfLoads + 1 > this.projectService.maxNumberOfLoads;
    if (isLoadLimitReached && which === 'plus') {
      this.modalService.open(MaxLoadsPopupComponent).subscribe();
    } else {
      this.editLoadQuantity(which === 'plus' ? data.length : -data.length);
    }
  }

  private createObservableForMinusEvent(element: ElementRef<HTMLElement>) {
    const minus = fromEvent(element.nativeElement, 'click');
    this.subscriptions.push(
      minus
        .pipe(
          map(() => this.quantity--),
          bufferWhen(() => minus.pipe(debounceTime(300))),
        )
        .subscribe((data) => {
          this.plusMinus(data, 'minus');
        }),
    );
  }
}
